/**
 * Default Technical Log Book definition and field types for definition editor and entry form.
 * Keep in sync with backend constants/technicalLogBookDefinition.js.
 * Fallback when format.definition is null; also used to seed new format definitions.
 */

export const MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS = 5;
export const DEFAULT_SECTORS_PER_TECHLOGBOOK = 3;

export const USAGE_VALUE_TYPE_OPTIONS = [
  { value: 'HOURS', label: 'Hours' },
  { value: 'CYCLES', label: 'Cycles' },
  { value: 'LANDINGS', label: 'Landings' },
  { value: 'MONTHS', label: 'Months' },
  { value: 'YEARS', label: 'Years' },
  { value: 'DAYS', label: 'Days' }
];

export const COMPONENT_DUE_VALUE_TYPE_OPTIONS = USAGE_VALUE_TYPE_OPTIONS;

export const MAINTENANCE_AT_VALUE_TYPE_OPTIONS = [
  { value: 'HOURS', label: 'Hours' },
  { value: 'CYCLES', label: 'Cycles' },
  { value: 'LANDINGS', label: 'Landings' }
];

export const HOBBS_METER_RECORDS_GROUP_ID = 'hobbs-meter-records';

/** Built-in sector field forced onto all techlog formats (pilot IR / IAP currency). */
export const DEFAULT_IAP_COUNT_FIELD = {
  path: 'iapCount',
  label: 'IAP Count',
  type: 'number',
  visible: true,
  required: false,
  readOnly: false
};

export function sectorHasIapCountField(fields) {
  if (!Array.isArray(fields)) return false;
  return fields.some((field) => field?.path === 'iapCount');
}

/**
 * Ensure sector.fields includes IAP Count after Landings (or at end if Landings missing).
 * @param {object[]|null|undefined} fields
 * @returns {object[]}
 */
export function ensureIapCountSectorField(fields) {
  const next = Array.isArray(fields) ? [...fields] : [];
  if (sectorHasIapCountField(next)) return next;
  const landingsIdx = next.findIndex((field) => field?.path === 'landings');
  const field = { ...DEFAULT_IAP_COUNT_FIELD };
  if (landingsIdx >= 0) next.splice(landingsIdx + 1, 0, field);
  else next.push(field);
  return next;
}

export const DEFAULT_HOBBS_METER_RECORDS_SECTION = {
  id: HOBBS_METER_RECORDS_GROUP_ID,
  label: 'HOBBS METER RECORDS',
  fields: [
    { key: 'hobbs', label: 'HOBBS METER', type: 'number', visible: true, required: false, options: [] },
    { key: 'hobbsAC', label: 'HOBBS METER AC', type: 'number', visible: true, required: false, options: [] }
  ]
};

function cloneHobbsMeterRecordsSection() {
  return {
    ...DEFAULT_HOBBS_METER_RECORDS_SECTION,
    fields: DEFAULT_HOBBS_METER_RECORDS_SECTION.fields.map((field) => ({ ...field }))
  };
}

export function sectorHasHobbsMeterRecords(customFieldSections) {
  if (!Array.isArray(customFieldSections)) return false;
  return customFieldSections.some((group) => {
    if (!group) return false;
    if (group.id === HOBBS_METER_RECORDS_GROUP_ID) return true;
    if (/hobbs/i.test(String(group.label || ''))) return true;
    return (group.fields || []).some((field) => field?.key === 'hobbs' || field?.key === 'hobbsAC');
  });
}

export function ensureHobbsMeterRecordsSection(customFieldSections) {
  const sections = Array.isArray(customFieldSections) ? [...customFieldSections] : [];
  if (sectorHasHobbsMeterRecords(sections)) return sections;
  return [...sections, cloneHobbsMeterRecordsSection()];
}

export const DEFAULT_HOBBS_METER_TOTALS_FIELDS = [
  { path: 'totalBroughtForward.hobbs', label: 'HOBBS METER', type: 'number', visible: true, required: false, readOnly: false },
  { path: 'totalBroughtForward.hobbsAC', label: 'HOBBS METER AC', type: 'number', visible: true, required: false, readOnly: false },
  { path: 'totalCarriedForward.hobbs', label: 'HOBBS METER (Carried)', type: 'number', visible: true, required: false, readOnly: false },
  { path: 'totalCarriedForward.hobbsAC', label: 'HOBBS METER AC (Carried)', type: 'number', visible: true, required: false, readOnly: false }
];

export function isHobbsTotalsFieldPath(path) {
  return /\.hobbs(AC)?$/.test(String(path || ''));
}

export function totalsHasHobbsMeterRecords(fields) {
  if (!Array.isArray(fields)) return false;
  return fields.some((field) => isHobbsTotalsFieldPath(field?.path));
}

function insertHobbsMeterTotalsFields(fields) {
  const next = Array.isArray(fields) ? [...fields] : [];
  if (totalsHasHobbsMeterRecords(next)) return next;

  const hobbsBrought = DEFAULT_HOBBS_METER_TOTALS_FIELDS.filter((field) => field.path.startsWith('totalBroughtForward.'));
  const hobbsCarried = DEFAULT_HOBBS_METER_TOTALS_FIELDS.filter((field) => field.path.startsWith('totalCarriedForward.'));

  let lastBroughtIdx = -1;
  let lastCarriedIdx = -1;
  next.forEach((field, index) => {
    if (field.path?.startsWith('totalBroughtForward.')) lastBroughtIdx = index;
    if (field.path?.startsWith('totalCarriedForward.')) lastCarriedIdx = index;
  });

  if (lastBroughtIdx >= 0) next.splice(lastBroughtIdx + 1, 0, ...hobbsBrought.map((field) => ({ ...field })));
  else next.push(...hobbsBrought.map((field) => ({ ...field })));

  lastCarriedIdx = -1;
  next.forEach((field, index) => {
    if (field.path?.startsWith('totalCarriedForward.')) lastCarriedIdx = index;
  });

  if (lastCarriedIdx >= 0) next.splice(lastCarriedIdx + 1, 0, ...hobbsCarried.map((field) => ({ ...field })));
  else next.push(...hobbsCarried.map((field) => ({ ...field })));

  return next;
}

export function ensureHobbsMeterRecordsTotalsFields(totalsSection) {
  if (!totalsSection || totalsSection.id !== 'totals') return totalsSection;
  const fields = insertHobbsMeterTotalsFields(totalsSection.fields);
  if (fields === totalsSection.fields) return totalsSection;
  return { ...totalsSection, fields };
}

export function getTotalsSection(totalsSection) {
  return ensureHobbsMeterRecordsTotalsFields(totalsSection);
}

export const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'multiline', label: 'Multiline text' },
  { value: 'number', label: 'Number' },
  { value: 'hours', label: 'Hours (HH:MM)' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & time' },
  { value: 'time', label: 'Time' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'signature', label: 'Signature' },
  { value: 'file', label: 'File (upload / download)' },
  { value: 'dueValue', label: 'Due value (type + amount)' },
  { value: 'group', label: 'Group (nested)' },
  { value: 'array', label: 'Array (repeatable)' }
];

export const DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION = {
  version: '1',
  sections: [
    {
      id: 'header',
      label: 'Header',
      visible: true,
      fields: [
        { path: 'sheetNumber', label: 'Sheet Number', type: 'text', visible: true, required: true, readOnly: false },
        { path: 'operatorName', label: 'Operator Name', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'issueFormatRevision', label: 'Issue Format Revision', type: 'text', visible: true, required: false, readOnly: true },
        { path: 'aircraftType', label: 'Aircraft Type', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'aircraftRegistration', label: 'Aircraft Registration', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'aircraftSerialNumber', label: 'Aircraft Serial Number', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'entryDate', label: 'Entry Date', type: 'date', visible: true, required: false, readOnly: false },
        { path: 'baTestTime', label: 'BA Test Time', type: 'datetime', visible: true, required: false, readOnly: false },
        { path: 'maintenanceDetails', label: 'Maintenance Details', type: 'multiline', visible: true, required: false, readOnly: false },
        { path: 'workOrderNumber', label: 'Work Order Number', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'workOrderDate', label: 'Work Order Date', type: 'date', visible: true, required: false, readOnly: false },
        { path: 'crsNumber', label: 'CRS Number', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'crsDate', label: 'CRS Date', type: 'date', visible: true, required: false, readOnly: false },
        {
          path: 'crsValue',
          label: 'CRS Value',
          type: 'dueValue',
          visible: true,
          required: false,
          readOnly: false,
          options: USAGE_VALUE_TYPE_OPTIONS
        },
        { path: 'crsText', label: 'CRS Text', type: 'text', visible: true, required: false, readOnly: true },
        { path: 'amoName', label: 'AMO Name', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'techlogProof', label: 'Techlog proof', type: 'file', visible: true, required: false, readOnly: false },
        { path: 'whiteCopyReceived', label: 'White Copy (CAMO) Received', type: 'checkbox', visible: true, required: false, readOnly: false },
        { path: 'yellowCopyReceived', label: 'Yellow Copy (Operations) Received', type: 'checkbox', visible: true, required: false, readOnly: false },
        { path: 'pinkCopyReceived', label: 'Pink Copy (Aircraft) Received', type: 'checkbox', visible: true, required: false, readOnly: false }
      ],
      customFields: [],
      fieldSections: [
        {
          id: 'component-due',
          label: 'Component Due',
          visible: true,
          fields: [
            { path: 'componentDue.dueDate', label: 'Due Date', type: 'date', visible: true, required: false, readOnly: false },
            {
              path: 'componentDue',
              label: 'Due Value',
              type: 'dueValue',
              visible: true,
              required: false,
              readOnly: false,
              options: USAGE_VALUE_TYPE_OPTIONS
            }
          ]
        },
        {
          id: 'next-scheduled-maintenance',
          label: 'Next Scheduled Maintenance',
          visible: true,
          fields: [
            { path: 'nextScheduledMaintenance.description', label: 'Description', type: 'multiline', visible: true, required: false, readOnly: false },
            { path: 'nextScheduledMaintenance.dueDate', label: 'Due Date', type: 'date', visible: true, required: false, readOnly: false },
            {
              path: 'nextScheduledMaintenance.atValue',
              label: 'At Hrs/Cyc/Landings',
              type: 'dueValue',
              visible: true,
              required: false,
              readOnly: false,
              options: MAINTENANCE_AT_VALUE_TYPE_OPTIONS
            }
          ]
        }
      ]
    },
    {
      id: 'totals',
      label: 'Totals',
      visible: true,
      fields: [
        { path: 'totalBroughtForward.airframeHours', label: 'Airframe Hours', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.landings', label: 'Landings', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.engine1Hours', label: 'Engine 1 Hours', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.engine2Hours', label: 'Engine 2 Hours', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.lhEngineCyclesCT', label: 'LH Engine CT Cycles', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.lhEngineCyclesPT', label: 'LH Engine PT Cycles', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.lhEngineCyclesIMP', label: 'LH Engine IMP Cycles', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.rhEngineCyclesCT', label: 'RH Engine CT Cycles', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.rhEngineCyclesPT', label: 'RH Engine PT Cycles', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.rhEngineCyclesIMP', label: 'RH Engine IMP Cycles', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.lgLeftOvspdCy', label: 'LG LEFT OVSPD CY', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.lgRightOvspdCy', label: 'LG RIGHT OVSPD CY', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.nrAndHydraulicCycles', label: 'NR & HCY (NR & Hydraulic C...)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.lgNoseOvspdCy', label: 'LG NOSE OVSPD CY', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.lgExtRecCyc', label: 'LG EXT/REC CYC', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.diskWear', label: 'ROTOR BRAKE DISK WEAR', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.rotorBrakeRbcy', label: 'ROTOR BRAKE (RBCY)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.mgbTorqueTcy', label: 'MGB TORQUE (TCY)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.fuelConsumption', label: 'Total Fuel Consumption', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.rhEngineOilConsumption', label: 'Total Oil Consumption in RH Engine (ml)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.lhEngineOilConsumption', label: 'Total Oil Consumption in LH Engine (ml)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.hobbs', label: 'HOBBS METER', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalBroughtForward.hobbsAC', label: 'HOBBS METER AC', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.airframeHours', label: 'Airframe Hours (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.landings', label: 'Landings (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.engine1Hours', label: 'Engine 1 Hours (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.engine2Hours', label: 'Engine 2 Hours (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.lhEngineCyclesCT', label: 'LH Engine CT Cycles (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.lhEngineCyclesPT', label: 'LH Engine PT Cycles (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.lhEngineCyclesIMP', label: 'LH Engine IMP Cycles (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.rhEngineCyclesCT', label: 'RH Engine CT Cycles (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.rhEngineCyclesPT', label: 'RH Engine PT Cycles (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.rhEngineCyclesIMP', label: 'RH Engine IMP Cycles (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.lgLeftOvspdCy', label: 'LG LEFT OVSPD CY (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.lgRightOvspdCy', label: 'LG RIGHT OVSPD CY (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.nrAndHydraulicCycles', label: 'NR & HCY (NR & Hydraulic C...) (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.lgNoseOvspdCy', label: 'LG NOSE OVSPD CY (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.lgExtRecCyc', label: 'LG EXT/REC CYC (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.diskWear', label: 'ROTOR BRAKE DISK WEAR (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.rotorBrakeRbcy', label: 'ROTOR BRAKE (RBCY) (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.mgbTorqueTcy', label: 'MGB TORQUE (TCY) (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.flightAirTime', label: 'FLIGHT/AIR TIME (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.totalAirTime', label: 'Total Air Time (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.blockTime', label: 'Total Block Time (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.totalLandings', label: 'Total Landings (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.fuelConsumption', label: 'Total Fuel Consumption (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.rhEngineOilConsumption', label: 'Total Oil Consumption in RH Engine (Carried) (ml)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.lhEngineOilConsumption', label: 'Total Oil Consumption in LH Engine (Carried) (ml)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.hobbs', label: 'HOBBS METER (Carried)', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'totalCarriedForward.hobbsAC', label: 'HOBBS METER AC (Carried)', type: 'number', visible: true, required: false, readOnly: false }

      ],
      customFields: []
    },
    {
      id: 'sector',
      label: 'Sector',
      visible: true,
      sectorSlots: DEFAULT_SECTORS_PER_TECHLOGBOOK,
      fields: [
        { path: 'sectorNumber', label: 'Sector Number', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'picName', label: 'PIC Name', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'coPilotName', label: 'Co-Pilot Name', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'departurePlace', label: 'Departure Place', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'departureDate', label: 'Departure Date', type: 'date', visible: true, required: false, readOnly: false },
        { path: 'arrivalPlace', label: 'Arrival Place', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'arrivalDate', label: 'Arrival Date', type: 'date', visible: true, required: false, readOnly: false },
        { path: 'rotorStartTimeUTC', label: 'Rotor Start', type: 'time', visible: true, required: false, readOnly: false },
        { path: 'rotorStopTimeUTC', label: 'Rotor Stop', type: 'time', visible: true, required: false, readOnly: false },
        { path: 'blockTime', label: 'Block Time', type: 'text', visible: true, required: false, readOnly: true },
        { path: 'airboneTime', label: 'Airborne Time', type: 'text', visible: true, required: false, readOnly: false },
        { path: 'landings', label: 'Landings', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'iapCount', label: 'IAP Count', type: 'number', visible: true, required: false, readOnly: false },
        { path: 'natureOfFlight', label: 'Nature of Flight', type: 'dropdown', visible: true, required: false, readOnly: false, options: [{ value: 'N', label: 'N' }, { value: 'TF', label: 'TF' }, { value: 'NF', label: 'NF' }, { value: 'GR', label: 'GR' }] },
        { path: 'melReleased', label: 'MEL Released', type: 'dropdown', visible: false, required: false, readOnly: false, options: [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }] },
        { path: 'cruiseParameters', label: 'Cruise Parameters', type: 'group', visible: true, required: false, readOnly: false },
        { path: 'defectReport', label: 'Defect Report', type: 'group', visible: true, required: false, readOnly: false },
        { path: 'inspections', label: 'Inspections', type: 'array', visible: true, required: false, readOnly: false },
        { path: 'bffCertification', label: 'BFF Certification', type: 'group', visible: true, required: false, readOnly: false },
        { path: 'pilotAcceptance', label: 'Pilot Acceptance', type: 'group', visible: true, required: false, readOnly: false },
        { path: 'engineCleaningDetailsBFF', label: 'Engine Cleaning Details BFF', type: 'group', visible: true, required: false, readOnly: false }
      ],
      customFields: [],
      /** Named groups of sector custom fields (Define log book). Values still live in sector.customFields[key] (keys unique across groups). */
      customFieldSections: [cloneHobbsMeterRecordsSection()]
    },
    {
      id: 'additional',
      label: 'Additional',
      visible: true,
      fields: [],
      customFields: []
    }
  ]
};

/**
 * Maximum sectors allowed on one tech log book sheet (from format definition).
 * @param {object|null} definition - Format definition
 * @returns {number}
 */
export function getMaxSectorsPerTechlogBook(definition) {
  const sectorSection = definition?.sections?.find((s) => s.id === 'sector');
  const raw = sectorSection?.sectorSlots;
  const n = Number(raw);
  if (Number.isInteger(n) && n >= 1 && n <= MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS) return n;
  return DEFAULT_SECTORS_PER_TECHLOGBOOK;
}

/**
 * Resolve sector built-in fields for the entry form, ensuring IAP Count is present on all formats.
 * @param {object|null} sectorSection - Format definition section with id 'sector'
 * @returns {object[]}
 */
export function getSectorFields(sectorSection) {
  if (!sectorSection || sectorSection.id !== 'sector') return [];
  return ensureIapCountSectorField(sectorSection.fields);
}

/**
 * Resolve sector custom field groups for the entry form and editors.
 * Prefers customFieldSections; falls back to a single group from legacy customFields.
 * @param {object|null} sectorSection - Format definition section with id 'sector'
 * @returns {{ id: string, label: string, fields: object[] }[]}
 */
export function getSectorCustomFieldSections(sectorSection) {
  if (!sectorSection || sectorSection.id !== 'sector') return [];
  const sections = ensureHobbsMeterRecordsSection(sectorSection.customFieldSections);
  if (sections.length > 0) {
    return sections.map((s, i) => ({
      id: s.id || `cfs-${i}`,
      label: (s.label && String(s.label).trim()) ? s.label : `Group ${i + 1}`,
      fields: Array.isArray(s.fields) ? s.fields : []
    }));
  }
  const flat = sectorSection.customFields || [];
  if (!Array.isArray(flat) || flat.length === 0) return [];
  return [{ id: 'legacy', label: 'Sector custom fields', fields: flat }];
}

/**
 * Merge built-in header fields from the default definition into saved format definitions.
 * Inserts missing fields near their default position (e.g. CRS Value after CRS Date).
 * @param {object|null} headerSection
 * @returns {object[]}
 */
export function getHeaderFields(headerSection) {
  if (!headerSection || headerSection.id !== 'header') return [];
  const defaultHeader = DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION.sections.find((s) => s.id === 'header');
  const defaultFields = Array.isArray(defaultHeader?.fields) ? defaultHeader.fields : [];
  const savedFields = Array.isArray(headerSection.fields) ? [...headerSection.fields] : [];
  const existingPaths = new Set(savedFields.map((f) => f.path).filter(Boolean));

  for (const defField of defaultFields) {
    if (!defField.path || existingPaths.has(defField.path)) continue;
    const defaultIdx = defaultFields.findIndex((f) => f.path === defField.path);
    let insertAt = savedFields.length;
    for (let i = defaultIdx - 1; i >= 0; i -= 1) {
      const anchorPath = defaultFields[i]?.path;
      const anchorIdx = savedFields.findIndex((f) => f.path === anchorPath);
      if (anchorIdx >= 0) {
        insertAt = anchorIdx + 1;
        break;
      }
    }
    savedFields.splice(insertAt, 0, defField);
    existingPaths.add(defField.path);
  }

  return savedFields;
}

/**
 * Resolve header field groups (e.g. Component Due) for the entry form.
 * @param {object|null} headerSection - Format definition section with id 'header'
 * @returns {{ id: string, label: string, visible: boolean, fields: object[] }[]}
 */
export function getHeaderFieldSections(headerSection) {
  if (!headerSection || headerSection.id !== 'header') return [];
  const defaultHeader = DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION.sections.find((s) => s.id === 'header');
  const defaultSections = Array.isArray(defaultHeader?.fieldSections) ? defaultHeader.fieldSections : [];
  const savedSections = Array.isArray(headerSection.fieldSections) ? headerSection.fieldSections : [];
  const existingIds = new Set(savedSections.map((s) => s.id).filter(Boolean));
  const mergedSections = [
    ...savedSections,
    ...defaultSections.filter((s) => s.id && !existingIds.has(s.id))
  ];
  if (mergedSections.length === 0) return [];
  return mergedSections.map((s, i) => ({
    id: s.id || `hfs-${i}`,
    label: (s.label && String(s.label).trim()) ? s.label : `Section ${i + 1}`,
    visible: s.visible !== false,
    fields: Array.isArray(s.fields) ? s.fields : []
  }));
}
